import { useState } from "react";
import { api } from "@/lib/api";
import { HOME } from "@/constants/testIds";
import { toast } from "sonner";
import { MapPin, Mail, Phone, Clock } from "lucide-react";

const concerns = [
  "Baptism",
  "Confirmation",
  "Matrimony",
  "Anointing of the Sick",
  "Confession",
  "House Blessing",
  "Church Construction Donation",
  "Other",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    concern: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState("");

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.concern || !form.message) {
      toast.error("Please fill in all fields.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/inquiries", form);
      setSuccess(
        "Thank you. Your message has been received. The parish office will respond as soon as possible."
      );
      toast.success("Inquiry sent — pax et bonum.");
      setForm({ name: "", email: "", concern: "", message: "" });
    } catch (err) {
      toast.error("Could not send your message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-36 bg-parchment-100">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left: parish info */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-10 bg-sage" />
              <span className="text-[11px] uppercase tracking-[0.3em] text-sage-dark font-sans">
                Contact Us
              </span>
            </div>
            <h2 className="font-serif text-[clamp(2rem,4vw,3.2rem)] leading-[1.1] text-cocoa-900">
              The Parish Office
            </h2>
            <p className="mt-6 font-sans text-cocoa-800 font-light leading-relaxed max-w-md">
              Come pray with us, or reach out for sacramental requests,
              donations, and community concerns.
            </p>

            <div className="mt-10 space-y-6 font-sans text-cocoa-800">
              {[
                {
                  icon: MapPin,
                  label: "Address",
                  value: "Brgy. Mabuhay, General Santos City\nDiocese of Marbel",
                },
                { icon: Phone, label: "Phone", value: "0915 581 1419" },
                { icon: Mail, label: "Email", value: "sfap2028@gmail.com" },
                {
                  icon: Clock,
                  label: "Office Hours",
                  value: "Mon · Wed — Sat · 8:00 AM to 5:00 PM\nClosed Tuesdays & Sundays",
                },
              ].map(({ icon: Icon, label, value }) => (
                <div key={label} className="flex gap-5 items-start border-b border-parchment-400 pb-5">
                  <Icon className="text-terracotta mt-1 flex-shrink-0" size={20} strokeWidth={1.4} />
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.28em] text-sage-dark mb-1">
                      {label}
                    </div>
                    {label === "Phone" ? (
                      <a href="tel:+639155811419" className="text-cocoa-900 font-light hover:text-terracotta transition-colors">
                        {value}
                      </a>
                    ) : label === "Email" ? (
                      <a href="mailto:sfap2028@gmail.com" className="text-cocoa-900 font-light hover:text-terracotta transition-colors break-all">
                        {value}
                      </a>
                    ) : (
                      <div className="text-cocoa-900 whitespace-pre-line font-light">
                        {value}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-7">
            <div className="bg-parchment-50 border border-parchment-400 p-8 md:p-12">
              <h3 className="font-serif text-3xl text-cocoa-900 mb-2">
                Send an Inquiry
              </h3>
              <p className="font-sans text-cocoa-700 text-sm mb-8">
                Your message will be received privately by the parish office.
              </p>

              <form onSubmit={onSubmit} className="grid gap-5">
                <div className="grid md:grid-cols-2 gap-5">
                  <Field
                    label="Your name"
                    value={form.name}
                    onChange={onChange("name")}
                    testId={HOME.contactNameInput}
                  />
                  <Field
                    type="email"
                    label="Email address"
                    value={form.email}
                    onChange={onChange("email")}
                    testId={HOME.contactEmailInput}
                  />
                </div>

                <div>
                  <Label>Concern</Label>
                  <select
                    data-testid={HOME.contactConcernSelect}
                    value={form.concern}
                    onChange={onChange("concern")}
                    className="w-full bg-transparent border-0 border-b border-parchment-400 focus:border-terracotta py-3 font-sans text-cocoa-900 focus:outline-none transition-colors"
                  >
                    <option value="">Select a concern</option>
                    {concerns.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <Label>Message</Label>
                  <textarea
                    data-testid={HOME.contactMessageInput}
                    value={form.message}
                    onChange={onChange("message")}
                    rows={5}
                    placeholder="Write your message here…"
                    className="w-full bg-transparent border-0 border-b border-parchment-400 focus:border-terracotta py-3 font-sans text-cocoa-900 placeholder-cocoa-700/40 focus:outline-none transition-colors resize-none"
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid={HOME.contactSubmit}
                  className="mt-4 group inline-flex items-center justify-center gap-3 bg-cocoa-900 text-parchment-100 px-8 py-4 font-sans text-sm tracking-wide hover:bg-terracotta transition-colors disabled:opacity-60 w-full md:w-auto md:self-start"
                >
                  {submitting ? "Sending…" : "Submit Inquiry"}
                  <span className="transition-transform group-hover:translate-x-1">→</span>
                </button>

                {success && (
                  <p
                    data-testid={HOME.contactSuccess}
                    className="mt-2 font-serif italic text-sage-dark"
                  >
                    ✦ {success}
                  </p>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, value, onChange, testId, type = "text" }) {
  return (
    <div>
      <Label>{label}</Label>
      <input
        data-testid={testId}
        type={type}
        value={value}
        onChange={onChange}
        className="w-full bg-transparent border-0 border-b border-parchment-400 focus:border-terracotta py-3 font-sans text-cocoa-900 placeholder-cocoa-700/40 focus:outline-none transition-colors"
      />
    </div>
  );
}

function Label({ children }) {
  return (
    <label className="text-[10px] uppercase tracking-[0.28em] text-sage-dark font-sans block mb-1">
      {children}
    </label>
  );
}
